/*
 * CAPTURE-OWNED MODULE
 * Shared-word matching between a name the user is typing and a list of
 * existing items. Pure: no React, no API.
 *
 * Used by Template Studio's Add Field form to show similar standard fields
 * while the user types (and, later, by document type creation: it works on
 * any list of { key, label } items).
 *
 * Rules (decisions/capture/2026-10-17-template-studio-field-model-redesign.md,
 * decision 11):
 *   - names become lowercase word sets: split on anything that is not a-z or
 *     0-9 (underscores included), "#" reads as "number", and a small synonym
 *     map taken from live evidence rewrites known phrases;
 *   - overlap = shared words / words in the SHORTER name;
 *   - tiers, best first: exact (normalised key or label equal), contained
 *     (every word of the shorter name is in the longer one), partial (overlap
 *     of at least 0.5);
 *   - short generic input is NOT suppressed: typing "Date" is meant to list
 *     every field with the word date.
 */

/** Minimum overlap (shared words / words in the shorter name) for a partial match. */
export const PARTIAL_MIN_OVERLAP = 0.5;

/** How many matches a list shows before "Show all N". */
export const DEFAULT_VISIBLE_MATCHES = 6;

/**
 * Phrase synonyms, from live evidence only (2026-10-17 findings). Each entry
 * maps alias phrases onto one canonical phrase. Matching runs on word
 * sequences, so "sales tax" is rewritten but "tax id" is not.
 */
export const SYNONYMS = Object.freeze([
  { canonical: ['payment', 'terms'], aliases: [['terms']] },
  { canonical: ['purchase', 'order'], aliases: [['po'], ['p', 'o']] },
  { canonical: ['tax'], aliases: [['sales', 'tax']] },
  { canonical: ['total'], aliases: [['total', 'due'], ['amount', 'due'], ['grand', 'total']] },
]);

export const TIER_ORDER = Object.freeze(['exact', 'contained', 'partial']);

/**
 * Words too common to make a PARTIAL match on their own when the typed name
 * has two or more words: "PO Number" must not list Account Number or Bill
 * Number through "number" alone. Exact and contained matches ignore this, and
 * so does a one-word entry ("Date" still lists every date field).
 */
export const GENERIC_WORDS = Object.freeze(['number', 'date', 'name', 'total', 'type', 'id', 'code', 'amount']);

/** Lowercase word list: "#" reads as "number"; any run of other characters (underscores too) splits. */
export function words(text) {
  return String(text ?? '')
    .toLowerCase()
    .replace(/#/g, ' number ')
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
}

/** Key form for exact comparison: the words joined by "_" ("PO #" -> "po_number", "document_date" -> "document_date"). */
export function normalizeName(text) {
  return words(text).join('_');
}

// Synonym rules, longest alias first so "total due" wins over a shorter alias.
const RULES = SYNONYMS
  .flatMap((s) => s.aliases.map((alias) => ({ alias, canonical: s.canonical })))
  .sort((x, y) => y.alias.length - x.alias.length);

/** The rule whose alias starts at position i of the word list, if any. */
function ruleAt(list, i) {
  for (const r of RULES) {
    let hit = true;
    for (let j = 0; j < r.alias.length; j += 1) {
      if (list[i + j] !== r.alias[j]) { hit = false; break; }
    }
    if (hit) return r;
  }
  return null;
}

/** Rewrite synonym phrases (longest alias first), then return the word SET. */
export function wordSet(text) {
  const list = words(text);
  const out = [];
  let i = 0;
  while (i < list.length) {
    const rule = ruleAt(list, i);
    if (rule) {
      out.push(...rule.canonical);
      i += rule.alias.length;
    } else {
      out.push(list[i]);
      i += 1;
    }
  }
  return new Set(out);
}

/**
 * Compare one typed name with one item.
 * @param {{ key?: string, label?: string }} typed
 * @param {{ key: string, label?: string }} item
 * @returns {{ tier: 'exact'|'contained'|'partial', overlap: number, shared: number } | null}
 */
export function compareNames(typed, item, { genericWords = GENERIC_WORDS } = {}) {
  const typedKey = normalizeName(typed.key);
  const typedLabel = normalizeName(typed.label);
  const itemKey = normalizeName(item.key);
  const itemLabel = normalizeName(item.label);
  const exact = (typedKey && (typedKey === itemKey || typedKey === itemLabel))
    || (typedLabel && (typedLabel === itemKey || typedLabel === itemLabel));

  const a = wordSet(typed.label || typed.key);
  if (a.size === 0) return null;
  const generic = new Set(genericWords);
  const scores = [];
  for (const name of [item.label, item.key]) {
    const b = wordSet(name);
    if (b.size === 0) continue;
    let shared = 0;
    let specific = 0;
    for (const w of a) {
      if (!b.has(w)) continue;
      shared += 1;
      if (!generic.has(w)) specific += 1;
    }
    scores.push({ overlap: shared / Math.min(a.size, b.size), shared, specific, sizeGap: Math.abs(a.size - b.size) });
  }
  scores.sort((x, y) => y.overlap - x.overlap || y.specific - x.specific);
  const best = scores[0];
  const strip = ({ specific, ...rest }) => rest;
  if (exact) return { tier: 'exact', overlap: 1, shared: best?.shared ?? 0, sizeGap: 0 };
  if (!best || best.shared === 0) return null;
  if (best.overlap === 1) return { tier: 'contained', ...strip(best) };
  // Partial needs at least one shared word that is not generic. Only a
  // multi-word name can reach this line: with one word the overlap is 0 or 1.
  const partial = scores.find((sc) => sc.overlap >= PARTIAL_MIN_OVERLAP && sc.specific > 0);
  if (partial) return { tier: 'partial', ...strip(partial) };
  return null;
}

/**
 * Rank items against a typed name.
 * @param {{ key?: string, label?: string }} typed
 * @param {Array<{ key: string, label?: string }>} items
 * @param {{ genericWords?: string[] }} [options] passed to compareNames
 * @returns {Array<{ item, tier, overlap, shared }>} best first: tier, then overlap, then closer word count, then label
 */
export function matchNames(typed, items, options) {
  const out = [];
  for (const item of items || []) {
    const m = compareNames(typed, item, options);
    if (m) out.push({ item, tier: m.tier, overlap: m.overlap, shared: m.shared, sizeGap: m.sizeGap });
  }
  out.sort((x, y) =>
    TIER_ORDER.indexOf(x.tier) - TIER_ORDER.indexOf(y.tier)
    || y.overlap - x.overlap
    || x.sizeGap - y.sizeGap
    || String(x.item.label ?? x.item.key).localeCompare(String(y.item.label ?? y.item.key)));
  return out.map(({ sizeGap, ...rest }) => rest);
}
